import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { playbookChecklistWebSocketService } from '../services/playbookChecklistWebSocketService';
import JumpToLatestButton from './ui/JumpToLatestButton';

const HotButtonFocus: React.FC<{ canDismiss?: boolean }> = ({ canDismiss = true }) => {
  const { cards, addedCardId } = useSelector((state: RootState) => state.hotButton);
  const listRef = useRef<HTMLDivElement>(null);
  // The server keeps cards oldest first; show the newest on top, like Live Call Insights.
  const newestFirst = cards.slice().reverse();

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-card border border-hairline bg-surface shadow-sm">
      <div className="flex h-12 flex-shrink-0 items-center justify-between gap-2 border-b border-hairline bg-surface-subtle px-4">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="heading-4">Hot Buttons</h2>
          <span className="text-xs text-ink-muted">{cards.length}</span>
        </div>
        {cards.length > 0 && (
          <JumpToLatestButton onClick={() => listRef.current?.scrollTo({ top: 0, behavior: 'smooth' })} />
        )}
      </div>
      <div ref={listRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto p-2" aria-live="polite" aria-relevant="additions">
        {cards.length === 0 ? (
          <div className="flex h-full items-center justify-center px-2 text-center text-sm text-ink-muted">
            Customer opportunities will appear here.
          </div>
        ) : newestFirst.map(card => (
          <aside key={card.id} aria-label="Live call suggestion"
            className={`rounded-card border-l-4 border-l-signature bg-signature-tint px-3 py-3 ${card.id === addedCardId ? 'hot-button-arrive' : ''}`}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-signature-strong">
                  {card.customerName ? `For ${card.customerName}` : 'Customer opportunity'}
                </p>
                <p className="mt-1 text-lg font-semibold leading-snug text-ink">{card.title}</p>
                <p className="mt-2 text-base leading-body text-ink-secondary">
                  <span className="font-medium text-ink">You might ask:</span> {card.suggestion}
                </p>
              </div>
              {canDismiss && <button type="button" aria-label={`Dismiss suggestion for ${card.customerName || 'customer'}`}
                className="min-h-11 shrink-0 rounded-control px-2 text-sm font-medium text-signature-strong hover:bg-surface focus-visible:outline focus-visible:outline-2 focus-visible:outline-signature"
                onClick={() => playbookChecklistWebSocketService.dismissHotButton(card.id)}>Dismiss</button>}
            </div>
            <details className="mt-2 text-sm text-ink-muted">
              <summary className="cursor-pointer select-none">Why this came up</summary>
              <div className="mt-1 max-h-20 overflow-y-auto">
                <p>{card.why}</p><p className="mt-1 italic">“{card.quote}”</p>
              </div>
            </details>
          </aside>
        ))}
      </div>
    </div>
  );
};

export default HotButtonFocus;
